import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isOpenNow(date) {
  const day = date.getDay(); // 0 Sun ... 6 Sat
  const minutes = date.getHours() * 60 + date.getMinutes();
  const open = 9 * 60;
  const close = 17 * 60;
  if (day === 0) return false; // Sunday closed
  return minutes >= open && minutes < close;
}

export default function Hours() {
  const { t } = useLanguage();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const open = isOpenNow(now);
  const todayIdx = now.getDay();

  return (
    <section
      id="hours"
      data-testid="hours-section"
      className="relative section-pad bg-[#0a0a0a]"
    >
      <div className="absolute inset-x-0 top-0 gold-divider" />
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <span className="text-overline">{t("hours.overline")}</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-tight">
              <span className="text-white">{t("hours.title").split(" ")[0]} </span>
              <span className="gold-gradient-text italic">
                {t("hours.title").split(" ").slice(1).join(" ")}
              </span>
            </h2>
            <p className="mt-5 text-sm text-white/60 max-w-md">
              {t("hours.footnote")}
            </p>

            <div
              data-testid="open-status-pill"
              className={`mt-8 inline-flex items-center gap-3 px-5 py-2.5 rounded-full border backdrop-blur-md transition-all ${
                open
                  ? "border-[#d4af37] bg-[rgba(212,175,55,0.12)] text-[#ffd700] shadow-[0_0_22px_rgba(212,175,55,0.35)]"
                  : "border-white/15 bg-white/5 text-white/55"
              }`}
            >
              <span className="relative">
                <span
                  className={`block w-2.5 h-2.5 rounded-full ${
                    open ? "bg-[#ffd700] pulse-dot" : "bg-white/40"
                  }`}
                />
              </span>
              <span className="text-xs tracking-[0.3em] uppercase font-semibold">
                {open ? t("hours.open_now") : t("hours.closed_now")}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="card-surface p-8 md:p-10 relative">
              <div className="flex items-center gap-3 mb-7 text-[#d4af37]">
                <Clock size={18} strokeWidth={1.5} />
                <span className="text-overline !text-[#d4af37]">
                  {now.toLocaleString(undefined, {
                    weekday: "long",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>

              <ul className="divide-y divide-[rgba(212,175,55,0.12)]">
                {DAY_KEYS.map((d, idx) => {
                  const active = idx === todayIdx;
                  const isSunday = d === "Sun";
                  return (
                    <li
                      key={d}
                      data-testid={`hours-row-${d}`}
                      className={`flex items-center justify-between py-4 transition-all ${
                        active
                          ? "text-[#ffd700]"
                          : "text-white/75"
                      }`}
                    >
                      <span
                        className={`font-serif-display text-lg md:text-xl ${
                          active ? "italic" : ""
                        }`}
                      >
                        {t(`hours.days.${d}`)}
                        {active && (
                          <span className="ml-3 inline-block w-2 h-2 rounded-full bg-[#ffd700] shadow-[0_0_10px_#ffd700] align-middle" />
                        )}
                      </span>
                      <span className="text-xs sm:text-sm tracking-[0.18em] uppercase font-medium">
                        {isSunday ? t("hours.time_sunday") : t("hours.time_weekdays")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
